/**
 * Face-size picker custom element for the PDP size modal.
 *
 * L'utente può scegliere “most sold” oppure una "taglia viso"
 * (narrow / average / wide). Il componente suggerisce una "taglia principale" e, se ha senso, una
 * "alternativa" nello stesso cluster. La conferma pubblica la scelta con `PUB_SUB_EVENTS.faceSizeSelection`.
 *
 * ## Contesto (mm e cluster)
 * Ogni variante ha una larghezza montatura in mm (`width`). Da quel numero si derivano uno o più
 * cluster `narrow`, `average`, `wide` tramite `FACE_SIZE_RANGE` e `faceClusterKeysForWidthMm`.
 * I cluster possono sovrapporsi. Scegliendo un radio non si “misura” il viso: si filtra il catalogo
 * per taglie adatte a quel cluster.
 *
 * ## Passo 1 — Chi è candidato?
 * Per es. narrow: solo varianti il cui array `clusters` contiene `'narrow'`. L’ordine delle varianti
 * è quello del prodotto (JSON) e conta nei pareggi. Se nessuna variante è in quel cluster → piano B.
 *
 * ## Passo 2 — Taglia principale (`mostSuitableVariant`)
 * - "average": tra le candidate, `width` più vicina al midpoint in mm del cluster average;
 *   a parità di distanza dal midpoint → resta chi compare prima nel catalogo (si cambia candidato solo se
 *   è più vicino, non se è ugualmente vicino — `pickClosestToMidpoint`).
 * - "narrow": larghezza minima; a parità di mm → vince chi è dopo nel catalogo (il confronto usa `<=`,
 *   quindi a width uguale la scelta “scorre” alla variante successiva — `pickPrimaryWidthExtreme` in modalità min).
 * - "wide": larghezza massima; stesso schema con `>=` (a parità vince l’ultima nell’ordine prodotto —
 *   `pickPrimaryWidthExtreme` in modalità max).
 *
 * Piano B (cluster vuoto): primaria = variante più vicina al midpoint su tutte le SKU
 * (`pickGlobalClosestToAverageMidpoint`); nessuna alternativa; copy di avviso per narrow/wide cluster.
 *
 * Percorso “most sold”: primaria = `mostSoldVariant`; nessuna seconda da cluster
 * (`applyMostSoldSelection`).
 *
 * ## Passo 3 — Alternativa (`secondarySuggestedVariant`)
 * Solo se ci sono almeno due varianti nel cluster: si rimuove la primaria, si ordinano le rimanenti
 * (average: distanza dal midpoint; narrow: width crescente; wide: decrescente), si prende la prima dopo
 * il sort (= seconda migliore). Se l’alternativa ha la stessa larghezza della primaria, il link non ha
 * senso e l’UI non lo mostra.
 *
 * ## Dati (Liquid + DOM)
 * - `#product_face_size_variants` — JSON varianti non-RX; `clusters` calcolato in JS da `width`.
 * - `#size_label_script` — soglie mm → sottotitoli “fitting” tradotti.
 * - `data-most-sold-variant-id` sul radio “most sold”.
 *
 * ## Main pieces of runtime state
 * | Property | Meaning |
 * |----------|---------|
 * | `variants` | All picker-eligible SKUs in product order (order matters for tie-breaks). |
 * | `mostSoldVariant` | Business “hero” SKU from metafields / analytics (via radio data attr). |
 * | `mostSuitableVariant` | Primary suggestion — what confirm sends and what the UI shows first. |
 * | `secondarySuggestedVariant` | Second choice in the same face cluster (powers the “alternative size” link), or `{}`. |
 * | `currentRange` | `FACE_SIZE_RANGE` entry while a cluster radio is logically active; `{}` on “usually purchased”. |
 * | `isVariantInSelectedRange` | `false` when the chosen cluster has no variants and we fall back globally. |
 *
 * ## External DOM sync
 * Elements `[data-sizecontent]` and `[data-size-fitting-label]` live outside this element,
 * under `.po-product-sizes-container` when possible, so inline PDP labels stay in sync with the modal.
 */

/**
 * Millimetre bands for each face cluster. Single source for:
 * - `currentRange` when a cluster radio is selected (`FACE_SIZE_RANGE[key]`).
 * - Average midpoint for “closest to average” primary vs secondary logic.
 * - Cluster membership per variant via faceClusterKeysForWidthMm (overlapping ranges allowed).
 */
const FACE_SIZE_RANGE = {
  narrow: { key: 'narrow', min: 111, max: 128 },
  average: { key: 'average', min: 123, max: 138 },
  wide: { key: 'wide', min: 133, max: 150 },
};

/**
 * Returns which face clusters a frame width (mm) falls into, in order narrow → average → wide.
 * Bands may overlap; keys use `FACE_SIZE_RANGE[*].min` / `max` only — edit those to change behaviour.
 *
 * @param {number|string|null|undefined} widthMm — Typically `variant.metafields.custom.frame_width` from JSON.
 * @returns {string[]} e.g. `['narrow','average']` for 125 mm; `[]` if missing or non-finite.
 */
function faceClusterKeysForWidthMm(widthMm) {
  if (widthMm == null || widthMm === '') return [];
  const w = Number(widthMm);
  if (!Number.isFinite(w)) return [];

  const { narrow, average, wide } = FACE_SIZE_RANGE;
  const keys = [];
  if (w >= narrow.min && w <= narrow.max) keys.push(narrow.key);
  if (w >= average.min && w <= average.max) keys.push(average.key);
  if (w >= wide.min && w <= wide.max) keys.push(wide.key);
  return keys;
}

/**
 * `<face-size-picker>` — prepare variant JSON, cluster math, and all modal UI updates.
 */
class FaceSizePicker extends HTMLElement {
  /**
   * Loads variant JSON, caches static element references inside the shadow tree’s host subtree.
   */
  constructor() {
    super();

    // --- Embedded JSON (Liquid-generated) ---
    this.sizeLabelScript = this.querySelector('script[type="application/json"]#size_label_script');

    // --- Selection state (filled/updated in connectedCallback and event handlers) ---
    this.mostSoldVariant = {};
    this.mostSuitableVariant = {};
    this.secondarySuggestedVariant = {};
    this.variants = [];
    this.currentRange = {};
    this.loadFaceSizeVariants();

    this.isVariantInSelectedRange = true;

    // --- Form controls ---
    this.mostSoldSizeInput = this.querySelector('.js-most-sold-face-size-input');
    this.sizePickersInput = this.querySelectorAll('.face-size-input');

    // --- Suggested size / warnings / alternative / OOS ---
    this.suggestedSizeContainer = this.querySelector('.js-suggested-size-container');
    this.suggestedSizeLabel = this.querySelector('.js-suggested-size-label');
    this.selectedSizeWidth = this.querySelector('.js-selected-size-width');
    this.sizeWarningContainer = this.querySelector('.js-size-warning');
    this.sizeWarningLabel = this.querySelector('.js-size-warning-label');
    this.alternativeSizeContainer = this.querySelector('.js-alternative-size-container');
    this.outOfStockContainer = this.querySelector('.js-out-of-stock-banner-container');

    this.sizeConfirmButton = this.querySelector('.js-size-confirm-btn');

    /** Set `true` to log selection state to the console while debugging. */
    this.debugMode = false;
  }

  /**
   * 1. Resolves sync root for PDP labels (`closest` needs a connected tree).
   * 2. Builds `variantById` and a precomputed map of variants per cluster for fast lookups.
   * 3. Applies the default path: usually purchased → `applyMostSoldSelection`.
   * 4. Wires click handlers: cluster radios, confirm, usually purchased, alternative size link.
   */
  connectedCallback() {
    // PDP size row + modal share a wrapper; fallback to document if markup changes.
    const syncRoot = this.closest('.po-product-sizes-container') || document;
    this.sizeModalSpan = syncRoot.querySelectorAll('[data-sizecontent]');
    this.sizeModalFittingLabel = syncRoot.querySelectorAll('[data-size-fitting-label]');

    this.sizeLabelsParsed = this.sizeLabels;
    // Descending numeric order: first match in the loop = highest threshold the width satisfies.
    this.sizeThresholds = Object.keys(this.sizeLabelsParsed).map(Number).sort((a, b) => b - a);
    this.variantById = new Map(this.variants.map((v) => [String(v.id), v]));
    this.rebuildVariantsByClusterKey();

    this.mostSoldVariant = this.resolveMostSoldVariant();
    this.applyMostSoldSelection();

    // Narrow / average / wide: each `value` matches a key on FACE_SIZE_RANGE.
    this.sizePickersInput.forEach((sizePicker) => {
      sizePicker.addEventListener('click', (event) => {
        const radio = event.target;
        const selectedRange = FACE_SIZE_RANGE[radio.value];
        // Same object reference as `this.currentRange` → skip redundant work if user re-clicks the same band.
        if (this.currentRange === selectedRange) return;
        this.applyClusterSelection(radio);
        this.sizeConfirmButton?.setAttribute('data-option-face-id', radio.value);
      });
    });

    this.sizeConfirmButton?.addEventListener('click', (event) => {
      this.confirmSuggestedVariant(event);
    });

    this.mostSoldSizeInput?.addEventListener('click', () => {
      this.applyMostSoldSelection();
    });

    this.alternativeSizeContainer?.addEventListener('click', () => {
      this.promoteSecondaryToPrimary();
    });
  }

  /**
   * Reads and normalizes `#product_face_size_variants` into `this.variants`.
   *
   * Each row is one non-RX variant from Liquid. `id` is always stored as a string so Map lookups
   * match `dataset.mostSoldVariantId`. `clusters` is always derived from `width` via faceClusterKeysForWidthMm.
   *
   * On parse failure or invalid payload, `this.variants` becomes `[]` and an error is logged.
   *
   * @returns {void}
   */
  loadFaceSizeVariants() {
    const el = this.querySelector('script[type="application/json"]#product_face_size_variants');
    const raw = el?.textContent?.trim();
    if (!raw) {
      this.variants = [];
      return;
    }
    try {
      const data = JSON.parse(raw);
      if (!Array.isArray(data)) {
        this.variants = [];
        return;
      }
      this.variants = data.map((row) => ({
        id: String(row.id),
        width: row.width,
        model_size: row.model_size ?? '',
        bridge_width: row.bridge_width ?? '',
        available: row.available,
        frame_size_option_id: row.frame_size_option_id,
        title: row.title ?? '',
        seg_height: row.seg_height,
        lens_width: row.lens_width,
        temple_length: row.temple_length,
        clusters: faceClusterKeysForWidthMm(row.width),
      }));
    } catch (error) {
      console.error('FaceSizePicker: invalid #product_face_size_variants JSON', error);
      this.variants = [];
    }
  }

  /**
   * Maps the theme’s “most sold” variant id (on the usual-purchase radio) to a full variant object
   * from `this.variants`.
   *
   * @returns {Object} The matched variant, or the first variant as fallback, or `{}` if the list is empty.
   */
  resolveMostSoldVariant() {
    const id = this.mostSoldSizeInput?.dataset?.mostSoldVariantId?.trim();
    if (id && this.variantById?.has(String(id))) {
      return this.variantById.get(String(id));
    }
    if (this.variants.length > 0) return this.variants[0];
    return {};
  }

  /**
   * Picks a single cluster key for analytics/copy (`data-option-face-id`) when a variant can sit in
   * multiple bands. Priority is narrow → average → wide (same order `getFaceFitClusterKeyForVariant`
   * uses elsewhere in the theme).
   *
   * @param {Object} variant — Must carry `clusters: string[]`.
   * @returns {string} One of `'narrow' | 'average' | 'wide'`, or `''` if none apply.
   */
  getFaceFitClusterKeyForVariant(variant) {
    const priorityOrder = [
      FACE_SIZE_RANGE.narrow.key,
      FACE_SIZE_RANGE.average.key,
      FACE_SIZE_RANGE.wide.key,
    ];
    for (const clusterKey of priorityOrder) {
      if (variant.clusters?.includes(clusterKey)) return clusterKey;
    }
    return '';
  }

  /**
   * “Usually purchased” path — no active face band, no cluster-based secondary suggestion.
   *
   * - Clears `currentRange` and `secondarySuggestedVariant`.
   * - Sets primary suggestion to `mostSoldVariant`.
   * - Marks the shopper as “in range” for copy (`isVariantInSelectedRange`).
   * - Refreshes all UI via {@link FaceSizePicker#updateSuggestedVariantUiBox}.
   *
   * @returns {void}
   */
  applyMostSoldSelection() {
    this.currentRange = {};
    this.isVariantInSelectedRange = true;
    this.mostSuitableVariant = this.mostSoldVariant;
    this.secondarySuggestedVariant = {};
    const faceKey = this.getFaceFitClusterKeyForVariant(this.mostSoldVariant);
    if (faceKey) {
      this.sizeConfirmButton?.setAttribute('data-option-face-id', faceKey);
    }
    this.updateSuggestedVariantUiBox();
  }

  /**
   * Fills `_variantsByClusterKey`: for each of narrow/average/wide, every variant whose `clusters`
   * includes that key — same order as `this.variants` (needed for narrow/wide tie-break rules).
   *
   * Call again only if `this.variants` is ever mutated after init.
   *
   * @returns {void}
   */
  rebuildVariantsByClusterKey() {
    const clusterKeys = [
      FACE_SIZE_RANGE.narrow.key,
      FACE_SIZE_RANGE.average.key,
      FACE_SIZE_RANGE.wide.key,
    ];
    this._variantsByClusterKey = new Map(
      clusterKeys.map((key) => [key, this.variants.filter((v) => v.clusters?.includes(key))])
    );
  }

  /**
   * All variants that belong to the given face cluster (`clusters` contains `clusterKey`), in product order.
   *
   * @param {string} clusterKey — `'narrow' | 'average' | 'wide'` (radio `value`).
   * @returns {Object[]} Cached list, or `[]` if none / unknown key.
   */
  getVariantsInCluster(clusterKey) {
    return this._variantsByClusterKey?.get(clusterKey) ?? [];
  }

  /**
   * Centre of the average mm band, integer-rounded. Used as the target width for “average” cluster
   * primary/secondary selection and for the global fallback when a cluster has no variants.
   *
   * Result is memoized on `this._averageMidpointMm` for the lifetime of the instance.
   *
   * @returns {number}
   */
  getAverageMidpointMm() {
    if (this._averageMidpointMm === undefined) {
      this._averageMidpointMm = parseInt(
        (FACE_SIZE_RANGE.average.min + FACE_SIZE_RANGE.average.max) / 2,
        10
      );
    }
    return this._averageMidpointMm;
  }

  /**
   * Average-cluster helper: among `candidates`, pick the variant whose `width` is closest to `mid`.
   * Uses strict improvement (`d < bestD`); if two are equally close, the earlier one in the array wins.
   *
   * @param {Object[]} candidates — Non-empty expected by callers; empty → `null`.
   * @param {number} mid — Millimetres (typically {@link getAverageMidpointMm}).
   * @returns {Object|null}
   */
  pickClosestToMidpoint(candidates, mid) {
    if (!candidates.length) return null;
    let best = candidates[0];
    let bestDistance = Math.abs(Number(best.width) - mid);
    for (let i = 1; i < candidates.length; i++) {
      const candidate = candidates[i];
      const distance = Math.abs(Number(candidate.width) - mid);
      if (distance < bestDistance) {
        best = candidate;
        bestDistance = distance;
      }
    }
    return best;
  }

  /**
   * Narrow / wide primary helper: walk `variantsInCluster` in order and track the best width so far.
   * - `min`: prefer smaller `width`; `<=` means on ties the later SKU wins.
   * - `max`: prefer larger `width`; `>=` means on ties the later SKU wins.
   *
   * @param {Object[]} variantsInCluster — Non-empty list for that face cluster.
   * @param {'min'|'max'} direction
   * @returns {Object}
   */
  pickPrimaryWidthExtreme(variantsInCluster, direction) {
    const pickMinimum = direction === 'min';
    let best = variantsInCluster[0];
    let bestWidth = Number(best.width);
    for (let i = 1; i < variantsInCluster.length; i++) {
      const candidate = variantsInCluster[i];
      const width = Number(candidate.width);
      const isBetterOrEqual = pickMinimum ? width <= bestWidth : width >= bestWidth;
      if (isBetterOrEqual) {
        best = candidate;
        bestWidth = width;
      }
    }
    return best;
  }

  /**
   * Sort comparator used to pick the secondary variant after the primary is removed from the cluster list.
   * Stable sort: ties keep relative order from the original array.
   *
   * - average: ascending distance to {@link getAverageMidpointMm}.
   * - narrow: ascending width.
   * - wide: descending width.
   *
   * @param {string} clusterKey
   * @returns {(a: Object, b: Object) => number}
   */
  getSecondaryVariantComparator(clusterKey) {
    if (clusterKey === FACE_SIZE_RANGE.average.key) {
      const mid = this.getAverageMidpointMm();
      return (a, b) =>
        Math.abs(Number(a.width) - mid) - Math.abs(Number(b.width) - mid);
    }
    if (clusterKey === FACE_SIZE_RANGE.narrow.key) {
      return (a, b) => Number(a.width) - Number(b.width);
    }
    if (clusterKey === FACE_SIZE_RANGE.wide.key) {
      return (a, b) => Number(b.width) - Number(a.width);
    }
    return () => 0;
  }

  /**
   * Empty-cluster fallback: when the shopper’s cluster has no SKUs, we still suggest something:
   * the variant whose width is closest to the average midpoint across all `this.variants`.
   *
   * @returns {Object} `{}` if there are no variants at all.
   */
  pickGlobalClosestToAverageMidpoint() {
    if (this.variants.length === 0) return {};
    const picked = this.pickClosestToMidpoint(this.variants, this.getAverageMidpointMm());
    return picked ?? {};
  }

  /**
   * Chooses the primary variant inside a non-empty `variantsInCluster` list.
   *
   * | Cluster | Rule |
   * |---------|------|
   * | average | Closest width to average midpoint; ties → earlier in the list. |
   * | narrow | Smallest width; ties → later in the list. |
   * | wide | Largest width; ties → later in the list. |
   *
   * @param {string} clusterKey
   * @param {Object[]} variantsInCluster — All SKUs for this face cluster (product order).
   * @returns {Object|null} `null` only if the list is empty; unknown key falls back to `variantsInCluster[0]`.
   */
  pickPrimaryVariantInCluster(clusterKey, variantsInCluster) {
    if (!variantsInCluster.length) return null;

    if (clusterKey === FACE_SIZE_RANGE.average.key) {
      return this.pickClosestToMidpoint(variantsInCluster, this.getAverageMidpointMm());
    }
    if (clusterKey === FACE_SIZE_RANGE.narrow.key) {
      return this.pickPrimaryWidthExtreme(variantsInCluster, 'min');
    }
    if (clusterKey === FACE_SIZE_RANGE.wide.key) {
      return this.pickPrimaryWidthExtreme(variantsInCluster, 'max');
    }

    return variantsInCluster[0];
  }

  /**
   * Second suggested variant in the same cluster after removing the primary (by `id`).
   * Sorts a copy of the remainder so the cached map in `_variantsByClusterKey` is never mutated.
   *
   * @param {string} clusterKey
   * @param {string|number} primaryId
   * @param {Object[]} variantsInCluster — Full list for the cluster (includes the primary).
   * @returns {Object} `{}` if no secondary variant exists.
   */
  pickSecondaryVariantInCluster(clusterKey, primaryId, variantsInCluster) {
    const withoutPrimary = variantsInCluster.filter((v) => v.id !== primaryId);
    if (!withoutPrimary.length) return {};
    const compare = this.getSecondaryVariantComparator(clusterKey);
    return [...withoutPrimary].sort(compare)[0];
  }

  /**
   * Applies a face cluster from the clicked radio (`narrow` | `average` | `wide`).
   *
   * - Sets `currentRange` to the matching `FACE_SIZE_RANGE` entry.
   * - If this cluster has variants: primary + secondary from {@link pickPrimaryVariantInCluster} / {@link pickSecondaryVariantInCluster}.
   * - If the cluster list is empty: `isVariantInSelectedRange = false`, primary = global fallback, no secondary;
   *   {@link updateSizeWarning} will show copy for narrow/wide (not average).
   *
   * Always ends with a full UI refresh.
   *
   * @param {HTMLInputElement} radioInput — `.face-size-input`
   * @returns {void}
   */
  applyClusterSelection(radioInput) {
    const clusterKey = radioInput.value;
    this.currentRange = FACE_SIZE_RANGE[clusterKey];
    const variantsInCluster = this.getVariantsInCluster(clusterKey);

    if (variantsInCluster.length > 0) {
      const primary = this.pickPrimaryVariantInCluster(clusterKey, variantsInCluster);
      this.mostSuitableVariant = primary || {};
      this.isVariantInSelectedRange = true;
      this.secondarySuggestedVariant =
        primary && primary.id
          ? this.pickSecondaryVariantInCluster(clusterKey, primary.id, variantsInCluster)
          : {};
    } else {
      this.isVariantInSelectedRange = false;
      this.mostSuitableVariant = this.pickGlobalClosestToAverageMidpoint();
      this.secondarySuggestedVariant = {};
    }

    this.updateSuggestedVariantUiBox();
  }

  /**
   * Label prefix for the “alternative size” link, based on `secondarySuggestedVariant` vs primary width:
   * uses `data-alternative-wide` or `data-alternative-narrow` on the alternative container.
   *
   * @returns {string}
   */
  get alternativeText() {
    let text = '';
    if (this.secondarySuggestedVariant?.width) {
      const alternativeIsWider =
        Number(this.secondarySuggestedVariant.width) > Number(this.mostSuitableVariant.width);
      text = alternativeIsWider
        ? this.alternativeSizeContainer.dataset.alternativeWide
        : this.alternativeSizeContainer.dataset.alternativeNarrow;
    }
    return text;
  }

  /**
   * When the shopper picked narrow/wide but no SKU exists in that cluster, we show an informational
   * message: narrow → “size too wide” copy, wide → “size too narrow” (inverse framing).
   *
   * @returns {string} HTML-safe string from Liquid `data-warning-*` attributes.
   */
  get sizeWarning() {
    if (!this.currentRange?.key || !this.sizeWarningContainer) return '';
    let warning = '';
    switch (this.currentRange.key) {
      case FACE_SIZE_RANGE.narrow.key:
        warning = this.sizeWarningContainer.dataset.warningWide ?? '';
        break;
      case FACE_SIZE_RANGE.wide.key:
        warning = this.sizeWarningContainer.dataset.warningNarrow ?? '';
        break;
      default:
        warning = '';
        break;
    }
    return warning;
  }

  /**
   * Parsed `#size_label_script` JSON: keys are numeric thresholds (as strings in JSON), values are translated labels.
   *
   * @returns {Object<string, string>}
   */
  get sizeLabels() {
    if (!this.sizeLabelScript) return {};
    try {
      return JSON.parse(this.sizeLabelScript.textContent);
    } catch (error) {
      console.error('Invalid size label JSON', error);
      return {};
    }
  }

  /**
   * Exposes the confirm button’s `data-option-value-id` as a one-element array for subscribers
   * (legacy shape expected by `faceSizeSelection` listeners).
   *
   * @returns {string[]}
   */
  get selectedOptionValues() {
    return Array.of(this.sizeConfirmButton).filter(Boolean).map(({ dataset }) => dataset.optionValueId);
  }

  /**
   * Alternative size link: makes the secondary suggestion the new primary and recomputes the next secondary.
   *
   * - No `currentRange.key` (usually-purchased path): promote `secondarySuggestedVariant` to primary, clear secondary,
   *   re-derive `data-option-face-id` from the new primary’s clusters.
   * - Cluster active & secondary still in that cluster’s variant list: promote secondary, pick a new secondary via {@link pickSecondaryVariantInCluster}.
   * - Secondary no longer in that list: re-run {@link applyClusterSelection} from the stored cluster radio
   *   (recovery path).
   *
   * Does not change which radio is checked — only internal state + UI.
   *
   * @returns {void}
   */
  promoteSecondaryToPrimary() {
    if (!this.secondarySuggestedVariant?.width) return;

    const activeClusterKey = this.currentRange?.key;
    if (!activeClusterKey) {
      this.mostSuitableVariant = this.secondarySuggestedVariant;
      this.secondarySuggestedVariant = {};
      const faceKey = this.getFaceFitClusterKeyForVariant(this.mostSuitableVariant);
      if (faceKey) {
        this.sizeConfirmButton?.setAttribute('data-option-face-id', faceKey);
      }
      this.updateSuggestedVariantUiBox();
      return;
    }

    const variantsInCluster = this.getVariantsInCluster(activeClusterKey);
    const alternativeStillInCluster = variantsInCluster.some(
      (v) => String(v.id) === String(this.secondarySuggestedVariant.id)
    );

    if (alternativeStillInCluster) {
      this.mostSuitableVariant = this.secondarySuggestedVariant;
      this.isVariantInSelectedRange = true;
      this.secondarySuggestedVariant = this.pickSecondaryVariantInCluster(
        activeClusterKey,
        this.mostSuitableVariant.id,
        variantsInCluster
      );
    } else {
      const radio = Array.from(this.sizePickersInput).find((r) => r.value === activeClusterKey);
      if (radio) {
        this.applyClusterSelection(radio);
        this.sizeConfirmButton?.setAttribute('data-option-face-id', activeClusterKey);
      }
      return;
    }

    this.updateSuggestedVariantUiBox();
  }

  /**
   * Fires when the shopper confirms. Other scripts listen on `PUB_SUB_EVENTS.faceSizeSelection`.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  confirmSuggestedVariant(event) {
    const target = event.currentTarget;

    publish(PUB_SUB_EVENTS.faceSizeSelection, {
      data: {
        event,
        target,
        selectedOptionValues: this.selectedOptionValues,
        variant: this.mostSuitableVariant,
      },
    });
  }

  /**
   * Central UI sync after any selection change: OOS banner, suggested size block, confirm `data-*`,
   * inline PDP spans, fitting label, warning strip, alternative link.
   *
   * @returns {void}
   */
  updateSuggestedVariantUiBox() {
    this.checkOutOfStock();

    if (this.debugMode) {
      console.log('currentRange', this.currentRange);
      console.log('[FaceSizePicker] mostSuitableVariant →', {
        title: this.mostSuitableVariant.title,
        width: this.mostSuitableVariant.width,
        clusters: this.mostSuitableVariant.clusters,
      });
      if (this.secondarySuggestedVariant?.title) {
        console.log('[FaceSizePicker] secondary suggested variant →', {
          title: this.secondarySuggestedVariant.title,
          width: this.secondarySuggestedVariant.width,
        });
      }
    }

    if (this.suggestedSizeContainer) {
      this.suggestedSizeContainer.setAttribute('aria-hidden', 'false');
      if (this.selectedSizeWidth) {
        this.selectedSizeWidth.innerHTML = `${this.mostSuitableVariant.model_size}-${this.mostSuitableVariant.bridge_width}`;
      }
      this.updateSuggestedSizeLabel();
    }

    this.updateSizeWarning();

    if (this.sizeConfirmButton) {
      this.sizeConfirmButton.setAttribute(
        'data-option-value-id',
        `${this.mostSuitableVariant.frame_size_option_id}`
      );
      this.sizeConfirmButton.setAttribute(
        'data-option-size-id',
        `${this.mostSuitableVariant.model_size}-${this.mostSuitableVariant.bridge_width}`
      );
    }

    this.updateSizeModalContent(
      `${this.mostSuitableVariant.model_size}-${this.mostSuitableVariant.bridge_width}`
    );
    this.updateSizeFittingLabel(this.sizeModalFittingLabel, this.mostSuitableVariant.width);
    this.displayAlternativeSize();
  }

  /**
   * Sets the human-readable line above the suggested size (`data-label-in-range` vs `data-label-default`)
   * depending on whether we are showing a true cluster match or a fallback suggestion.
   *
   * @returns {void}
   */
  updateSuggestedSizeLabel() {
    if (!this.suggestedSizeLabel) return;

    const label = this.isVariantInSelectedRange
      ? this.suggestedSizeLabel.dataset.labelInRange
      : this.suggestedSizeLabel.dataset.labelDefault;

    if (this.debugMode) {
      console.log(
        `[FaceSizePicker] updateSuggestedSizeLabel → isVariantInSelectedRange: ${this.isVariantInSelectedRange}, label: "${label}"`
      );
    }

    this.suggestedSizeLabel.textContent = label;
  }

  /**
   * Shows the “also available slightly wider/narrower” control when a secondary suggestion exists and differs
   * in lens width from the primary. Hides and blurs otherwise.
   *
   * @returns {void}
   */
  displayAlternativeSize() {
    if (!this.alternativeSizeContainer) return;

    const hasDistinctSecondarySuggestion =
      this.secondarySuggestedVariant.model_size &&
      this.secondarySuggestedVariant.width !== this.mostSuitableVariant.width;

    if (hasDistinctSecondarySuggestion) {
      this.alternativeSizeContainer.setAttribute('aria-hidden', 'false');
      this.alternativeSizeContainer.innerHTML = `${this.alternativeText} (${this.secondarySuggestedVariant.model_size}-${this.secondarySuggestedVariant.bridge_width})`;
      this.alternativeSizeContainer.dataset.altFrameWidth = this.secondarySuggestedVariant.width;
    } else {
      this.alternativeSizeContainer.blur();
      this.alternativeSizeContainer.setAttribute('aria-hidden', 'true');
    }
  }

  /**
   * Displays {@link sizeWarning} when `isVariantInSelectedRange` is false and the dataset provides text;
   * hides the strip when in-range or when there is nothing to say.
   *
   * @returns {void}
   */
  updateSizeWarning() {
    if (!this.sizeWarningContainer || !this.sizeWarningLabel) return;
    if (this.isVariantInSelectedRange) {
      this.sizeWarningContainer.setAttribute('aria-hidden', 'true');
    } else {
      const warningText = this.sizeWarning;
      this.sizeWarningLabel.innerHTML = warningText ?? '';
      const shouldShow = String(warningText).trim().length > 0;
      this.sizeWarningContainer.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }
  }

  /**
   * Toggles the out-of-stock banner from `mostSuitableVariant.available` (boolean from JSON or legacy `'true'` string).
   *
   * @returns {void}
   */
  checkOutOfStock() {
    if (!this.outOfStockContainer) return;
    const availability = this.mostSuitableVariant.available;
    const inStock = availability === true || availability === 'true';
    this.outOfStockContainer.setAttribute('aria-hidden', inStock ? 'true' : 'false');
  }

  /**
   * Updates every `[data-sizecontent]` node under the sync root (PDP + modal) with the `model–bridge` string.
   *
   * @param {string} newContent — e.g. `"52-22"`.
   * @returns {void}
   */
  updateSizeModalContent(newContent) {
    if (this.sizeModalSpan.length > 0) {
      this.sizeModalSpan.forEach((span) => {
        span.innerText = newContent;
      });
    }
  }

  /**
   * Maps frame width (mm) to the first matching threshold label from `sizeLabelsParsed` / `sizeThresholds`.
   *
   * @param {NodeListOf<Element>|Element[]} labels
   * @param {number|string} frameWidth
   * @returns {void}
   */
  updateSizeFittingLabel(labels, frameWidth) {
    if (!labels || !frameWidth) return;

    let labelText = '';
    for (const threshold of this.sizeThresholds) {
      if (frameWidth >= threshold) {
        labelText = this.sizeLabelsParsed[threshold];
        break;
      }
    }

    labels.forEach((label) => {
      label.innerText = labelText;
    });
  }
}

if (!customElements.get('face-size-picker')) {
  customElements.define('face-size-picker', FaceSizePicker);
}
